'use client';

import { useTranslations } from 'next-intl';

import { useSiteUrl } from '@/components/Contexts/SiteUrlContext';
import { useViewerRole } from '@/components/Contexts/ViewerRoleContext';

import { SidebarPinItem } from './SidebarPinItem';
import { formatPagePathLabel, isHomePagePath } from './sidebarPins';

import type { DeviceFilter, PinPageGroup, SidebarMode } from './types';
import type { CommentUserInfo } from '@/types/feedback';

interface SidebarPinListProps {
  groups: PinPageGroup[];
  mode: SidebarMode;
  deviceFilter: DeviceFilter;
  commenterInfoMap: Record<string, CommentUserInfo>;
  selectedPinId?: string | null;
  onPinClick: (id: string) => void;
  onReopenPin?: (id: string) => void;
}

const EMPTY_STATE_KEYS: Record<SidebarMode, Record<DeviceFilter, string>> = {
  comment: {
    all: 'get-feedback-sidebar-emptyCommentMode',
    mobile: 'get-feedback-sidebar-emptyCommentModeMobile',
    desktop: 'get-feedback-sidebar-emptyCommentModeDesktop',
  },
  browse: {
    all: 'get-feedback-sidebar-emptyBrowseMode',
    mobile: 'get-feedback-sidebar-emptyBrowseModeMobile',
    desktop: 'get-feedback-sidebar-emptyBrowseModeDesktop',
  },
};

export function SidebarPinList({
  groups,
  mode,
  deviceFilter,
  commenterInfoMap,
  selectedPinId,
  onPinClick,
  onReopenPin,
}: SidebarPinListProps) {
  const t = useTranslations();
  const siteUrl = useSiteUrl();
  // `New` tracks what the owner hasn't read yet, so only the partner sees it.
  const { isPartner } = useViewerRole();

  if (groups.length === 0) {
    return (
      <div className="flex-1 overflow-y-auto">
        <p className="px-4 py-8 text-center text-sm text-muted-foreground">
          {t(EMPTY_STATE_KEYS[mode][deviceFilter])}
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {groups.map(([path, entries]) => (
        <section key={path}>
          <h3 className="sticky top-0 z-10 bg-muted/80 px-4 py-1.5 text-xs font-medium text-muted-foreground backdrop-blur-sm truncate border-b border-border">
            {isHomePagePath(path, siteUrl)
              ? t('get-feedback-sidebar-homePage')
              : formatPagePathLabel(path)}
          </h3>
          <ul className="divide-y divide-border">
            {entries.map(({ pin, pinNumber }) => (
              <SidebarPinItem
                key={pin.id}
                pin={pin}
                pinNumber={pinNumber}
                commenterName={
                  pin.commenterId
                    ? commenterInfoMap[pin.commenterId]?.userName
                    : undefined
                }
                isUnread={isPartner && pin.status === 'NEW'}
                isSelected={selectedPinId === pin.id}
                onPinClick={onPinClick}
                onReopen={
                  pin.status === 'RESOLVED' && onReopenPin
                    ? () => onReopenPin(pin.id)
                    : undefined
                }
              />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
